/**
 * Website → 🏠 Home (first tab): a light overview of the chosen person
 * (approved draft: docs/dashboard-library-draft.html), two cards per row:
 *   💊 Recent taken medicine — today and yesterday, one line per meal:
 *      "✓ All taken" (green), "2 missed" (red, with names), "Later" (grey)
 *   ❤️ Blood pressure — the last 5 readings (DD/MM/YY, time, reading, pulse, level)
 *   🗓️ Medicine schedule — Morning / Lunch / Dinner, and "at dialysis" (no "Other times")
 *   🔔 Coming up — the next tests, doctor visits, injections and vaccines
 *   🧪 Tests · 🛡️ Vaccines · 🩺 Doctor visits
 * It only shows what is already saved. Each card links to its tab.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  addDays, BP_LEVEL_TEXT, doseTimesFor, formatDateISO, formatTime12, injectionDueOn, linkedMedicine, localTodayISO,
  medicineActiveOn, parseDateISO, wantsReminder, slotTime, vaccinesDue, type DoseSlot, type Medicine, type VaccineRecord,
} from '@bhorosha/core';
import type { BhStore, BpReading } from '@bhorosha/data';
import { backend } from '../data';
import { dayEntries, type DoseEntry, type PersonData } from './model';
import { useMonitor } from './useMonitor';
import { fmtDayDMY } from '../ui/dates';

type Meal = 'morning' | 'lunch' | 'dinner' | 'other';
const MEAL: Record<Meal, [string, string]> = { morning: ['🌅', 'Breakfast'], lunch: ['☀️', 'Lunch'], dinner: ['🌙', 'Dinner'], other: ['💉', 'Other times'] };
const mealOf = (slot: string): Meal => (slot.startsWith('morning') ? 'morning' : slot.startsWith('lunch') ? 'lunch' : slot.startsWith('dinner') ? 'dinner' : 'other');
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const two = (n: number) => String(n).padStart(2, '0');
const go = (page: string) => (window as unknown as { switchAppPage?: (p: string) => void }).switchAppPage?.(page);
const nameOf = (d: DoseEntry) => `${d.dose.name}${d.dose.strength ? ' ' + d.dose.strength : ''}`;

function readJSON<T>(store: BhStore, key: string, fallback: T): T {
  try { const v = store.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}

function Card({ title, link, onLink, full, children }: { title: string; link?: string; onLink?: () => void; full?: boolean; children: ReactNode }) {
  return (
    <section className={`bh-dash-card${full ? ' full' : ''}`}>
      <div className="bh-dash-ch"><b>{title}</b>{link && <button className="bh-dash-link" onClick={onLink}>{link} →</button>}</div>
      {children}
    </section>
  );
}
const Chip = ({ tone, children }: { tone: 'ok' | 'bad' | 'warn' | 'grey' | 'blue'; children: ReactNode }) => <span className={`bh-dchip ${tone}`}>{children}</span>;
function DateBox({ iso }: { iso: string | null }) {
  const d = iso ? parseDateISO(iso) : null;
  return <div className="bh-dash-date">{d ? <>{two(d.getUTCDate())}<small>{MON[d.getUTCMonth()]}</small></> : '—'}</div>;
}

export function Dashboard({ store, familyId, active }: { store: BhStore; familyId: string; active: boolean }) {
  const today = localTodayISO();
  const yesterday = formatDateISO(addDays(parseDateISO(today)!, -1));
  const { snap, people, now } = useMonitor(store, familyId, yesterday, today, active);
  const [personId, setPersonId] = useState<string>(() => (window as unknown as { state?: { activePersonId?: string } }).state?.activePersonId || '');
  const [bp, setBp] = useState<BpReading[] | null>(null);

  useEffect(() => {
    const on = (e: Event) => { const id = (e as CustomEvent).detail; if (id) setPersonId(id); };
    window.addEventListener('bh:person', on);
    return () => window.removeEventListener('bh:person', on);
  }, []);
  const pd = people.find(p => p.person.id === personId) || people[0];

  // The last 5 readings (looks back up to a year).
  const bpKey = `${pd?.person.id}|${snap.bp.length}|${snap.bp.map(b => b.id).join(',')}`;
  useEffect(() => {
    if (!active || !pd) return;
    let live = true;
    backend.loadBpReadings(familyId, pd.person.id, formatDateISO(addDays(parseDateISO(today)!, -365)), today)
      .then(r => { if (live) setBp(r.slice().sort((a, b) => b.measuredAt.localeCompare(a.measuredAt)).slice(0, 5)); })
      .catch(() => { if (live) setBp([]); });
    return () => { live = false; };
  }, [active, familyId, bpKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;
  if (!pd) return <div className="bh-dash"><p className="bh-muted">No people yet. Add the person whose medicines you look after (person menu → + Add person).</p></div>;
  return (
    <div className="bh-dash">
      <div className="bh-dash-grid">
        <RecentMeds pd={pd} days={[today, yesterday]} snap={snap} now={now} />
        <BpCard readings={bp} />
        <Schedule pd={pd} today={today} />
        <ComingUp pd={pd} store={store} today={today} />
        <TestsCard pd={pd} store={store} today={today} />
        <VaccinesCard pd={pd} store={store} today={today} />
        <VisitsCard pd={pd} store={store} today={today} />
      </div>
    </div>
  );
}

// ---------------- 💊 Recent taken medicine ----------------
function RecentMeds({ pd, days, snap, now }: { pd: PersonData; days: string[]; snap: Parameters<typeof dayEntries>[2]; now: Date }) {
  return (
    <Card title="💊 Recent taken medicine" link="Daily log" onLink={() => go('log')}>
      {days.map((d, i) => {
        const doses = dayEntries(pd, d, snap, now).filter((e): e is DoseEntry => e.kind === 'dose');
        const meals = (['morning', 'lunch', 'dinner'] as Meal[]).map(m => ({ m, list: doses.filter(x => mealOf(String(x.dose.slot)) === m) })).filter(x => x.list.length);
        return (
          <div key={d}>
            <div className="bh-dash-dayh">{i === 0 ? 'Today' : 'Yesterday'} · {fmtDayDMY(d, 'long')}</div>
            {!meals.length && <div className="bh-dash-empty">No medicines on this day.</div>}
            {meals.map(({ m, list }) => {
              const missed = list.filter(x => x.status === 'missed');
              const notMarked = list.filter(x => x.status === 'due');
              const later = list.filter(x => x.status === 'later');
              const skipped = list.filter(x => x.status === 'skipped');
              const taken = list.filter(x => x.status === 'taken');
              const time = list.map(x => x.time).sort()[0];
              const tone = missed.length ? 'bad' : notMarked.length ? 'warn' : later.length === list.length ? 'grey' : 'ok';
              const detail = [missed.length && `Missed: ${missed.map(nameOf).join(', ')}`, notMarked.length && `Not marked yet: ${notMarked.map(nameOf).join(', ')}`,
                skipped.length && `Skipped: ${skipped.map(nameOf).join(', ')}`].filter(Boolean).join(' · ')
                || `${later.length === list.length ? formatTime12(time) + ' · ' : ''}${list.length} medicine${list.length === 1 ? '' : 's'}`;
              const chip = missed.length ? `${missed.length} missed`
                : notMarked.length ? `${notMarked.length} not marked`
                : later.length === list.length ? 'Later'
                : later.length ? `${taken.length} of ${list.length} taken`
                : taken.length === list.length ? '✓ All taken' : '✓ Done';
              return (
                <div key={m} className={`bh-dash-meal ${tone}`}>
                  <span className="bh-dash-mi">{MEAL[m][0]}</span>
                  <div className="bh-dash-mw"><b>{MEAL[m][1]}</b><small>{detail}</small></div>
                  <Chip tone={tone}>{chip}</Chip>
                </div>
              );
            })}
          </div>
        );
      })}
    </Card>
  );
}

// ---------------- ❤️ Blood pressure ----------------
function BpCard({ readings }: { readings: BpReading[] | null }) {
  const tone = (l: string) => (l === 'normal' ? 'ok' : l === 'very_high' ? 'bad' : l === 'high' ? 'warn' : 'blue') as 'ok' | 'bad' | 'warn' | 'blue';
  return (
    <Card title="❤️ Blood pressure · last 5 readings" link="All readings" onLink={() => go('bp')}>
      {readings === null ? <div className="bh-dash-empty">Loading…</div> : !readings.length ? <div className="bh-dash-empty">No readings yet.</div> : (
        <div className="bh-dash-bp">
          <div className="bh-dash-bph"><span>Date · time</span><span>BP <small>mmHg</small></span><span>Pulse</span><span>Level</span></div>
          {readings.map(r => {
            const d = new Date(r.measuredAt);
            return (
              <div key={r.id} className={`bh-dash-bpx ${tone(r.level)}`}>
                <span className="bh-dash-bdt"><b>{two(d.getDate())}/{two(d.getMonth() + 1)}/{String(d.getFullYear()).slice(2)}</b>
                  <small>{formatTime12(`${two(d.getHours())}:${two(d.getMinutes())}`)}</small></span>
                <span className="bh-dash-bv"><b>{r.systolic}</b><i>/</i>{r.diastolic}</span>
                <span className="bh-dash-bpu">{r.pulse ? `❤ ${r.pulse}` : '—'}</span>
                <span className="bh-dash-blv"><Chip tone={tone(r.level)}>{BP_LEVEL_TEXT[r.level]?.word || r.level}</Chip></span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------- 🗓️ Medicine schedule ----------------
const n = (v: unknown) => parseFloat(String(v ?? 0)) || 0;
const fmtAmt = (a: number) => (a === 0.5 ? '½' : a === 0.25 ? '¼' : a === 1.5 ? '1½' : String(a));
const medName = (m: Medicine) => `${m.name}${m.strength ? ` ${m.strength} ${m.strength_unit || ''}`.trimEnd() : ''}`;

function Schedule({ pd, today }: { pd: PersonData; today: string }) {
  const times = doseTimesFor(pd.person);
  const meds = pd.meds.filter(m => medicineActiveOn(m, today));
  const slotList = (meal: 'morning' | 'lunch' | 'dinner') => {
    const out: { name: string; amt: string; before: boolean; noDialysis: boolean }[] = [];
    for (const m of meds.filter(x => (x.schedule_type || 'daily') === 'daily')) {
      for (const side of ['before', 'after'] as const) {
        const a = n((m as Record<string, unknown>)[`${meal}_${side}`]);
        if (a > 0) out.push({ name: medName(m), amt: fmtAmt(a), before: side === 'before', noDialysis: !!m.skip_on_dialysis_day });
      }
    }
    return out;
  };
  const timeText = (meal: 'morning' | 'lunch' | 'dinner') => {
    const hasB = meds.some(m => n((m as Record<string, unknown>)[`${meal}_before`]) > 0), hasA = meds.some(m => n((m as Record<string, unknown>)[`${meal}_after`]) > 0);
    return [hasB && `Before ${formatTime12(slotTime(`${meal}_before` as DoseSlot, times))}`, hasA && `After ${formatTime12(slotTime(`${meal}_after` as DoseSlot, times))}`].filter(Boolean).join(' · ');
  };
  const dialysis = meds.filter(m => (m.schedule_type || 'daily') === 'dialysis').map(medName);
  const dd = Array.isArray(pd.person.dialysis_days) ? pd.person.dialysis_days.map(Number) : [];
  const dayNames = dd.sort().map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ');
  const Slot = ({ meal, icon, title }: { meal: 'morning' | 'lunch' | 'dinner'; icon: string; title: string }) => {
    const list = slotList(meal);
    return (
      <div className="bh-dash-slot">
        <h4>{icon} {title}</h4><div className="bh-dash-tm">{timeText(meal) || 'Nothing'}</div>
        <ul>{list.slice(0, 6).map((x, i) => <li key={i}>{x.name} · {x.amt}{x.before && <small> (before)</small>}{x.noDialysis && <span className="bh-dchip grey sm">not on dialysis days</span>}</li>)}
          {list.length > 6 && <li className="bh-muted">… {list.length - 6} more</li>}</ul>
      </div>
    );
  };
  return (
    <Card title="🗓️ Medicine schedule" link="Purchase Calculator" onLink={() => go('calc')} full>
      <div className="bh-dash-sched">
        <Slot meal="morning" icon="🌅" title="Morning" />
        <Slot meal="lunch" icon="☀️" title="Lunch" />
        <Slot meal="dinner" icon="🌙" title="Dinner" />
      </div>
      {dialysis.length > 0 && <div className="bh-dash-dia">🩺 At dialysis{dayNames ? ` (${dayNames})` : ''}: {dialysis.join(', ')}</div>}
    </Card>
  );
}

// ---------------- 🔔 Coming up / Tests / Vaccines / Visits ----------------
interface TestRec { id: string; date: string; time?: string; lab?: string; status?: string; prep?: string; items?: { name: string; value?: string; unit?: string }[] }
interface VisitRec { id: string; date: string; time?: string; doctor?: string; specialty?: string; clinic?: string; status?: string }
const pid = (pd: PersonData) => pd.person.id;
const testsOf = (store: BhStore, pd: PersonData) => readJSON<TestRec[]>(store, `mpc_tests_v1::${pid(pd)}`, []);
const visitsOf = (store: BhStore, pd: PersonData) => readJSON<VisitRec[]>(store, `mpc_visits_v1::${pid(pd)}`, []);
const vaxOf = (store: BhStore, pd: PersonData) => readJSON<VaccineRecord[]>(store, `mpc_vaccines_v1::${pid(pd)}`, []);
const testNames = (t: TestRec) => (t.items || []).map(i => i.name).join(', ') || 'Tests';
const daysFrom = (today: string, iso: string) => {
  const a = parseDateISO(iso), b = parseDateISO(today);
  return a && b ? Math.round((a.getTime() - b.getTime()) / 86400000) : NaN; // a broken saved date: no chip, no crash
};
const whenChip = (today: string, iso: string) => { const d = daysFrom(today, iso); return Number.isNaN(d) ? 'Planned' : d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : d < 0 ? `${-d} days ago` : `In ${d} days`; };

interface Upcoming { date: string; icon: string; title: string; sub: string; tone: 'blue' | 'warn' | 'bad' }
function ComingUp({ pd, store, today }: { pd: PersonData; store: BhStore; today: string }) {
  const items = useMemo(() => {
    const out: Upcoming[] = [];
    for (const t of testsOf(store, pd)) if (t.status === 'scheduled' && t.date >= today) out.push({ date: t.date, icon: '🧪', title: testNames(t), sub: [t.time && formatTime12(t.time), t.lab, t.prep].filter(Boolean).join(' · '), tone: 'blue' });
    for (const v of visitsOf(store, pd)) if ((!v.status || v.status === 'scheduled') && v.date >= today) out.push({ date: v.date, icon: '🩺', title: v.doctor || 'Doctor visit', sub: [v.time && formatTime12(v.time), v.specialty, v.clinic].filter(Boolean).join(' · '), tone: 'blue' });
    for (const r of pd.reminders) {
      if (r.active === false || !wantsReminder(r, true)) continue;
      const lm = linkedMedicine(r, pd.meds);
      if (lm && (lm.schedule_type || 'daily') === 'dialysis') continue;
      for (let i = 0; i < 31; i++) {
        const d = formatDateISO(addDays(parseDateISO(today)!, i));
        if (injectionDueOn(r, d)) { out.push({ date: d, icon: '💉', title: `${r.name}${r.dose ? ' ' + r.dose : ''}`, sub: formatTime12(r.time || '09:00'), tone: 'blue' }); break; }
      }
    }
    for (const v of vaccinesDue(pd.person, vaxOf(store, pd), today, 60)) out.push({ date: v.due < today ? today : v.due, icon: '🛡️', title: v.name, sub: v.status === 'overdue' ? `Overdue since ${fmtDayDMY(v.due)}` : v.label || 'Vaccine due', tone: v.status === 'overdue' ? 'bad' : 'warn' });
    return out.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6);
  }, [pd, store, today]);
  return (
    <Card title="🔔 Coming up">
      {!items.length && <div className="bh-dash-empty">Nothing planned in the next weeks.</div>}
      {items.map((x, i) => (
        <div key={i} className="bh-dash-item"><DateBox iso={x.date} /><div className="bh-dash-iw"><b>{x.icon} {x.title}</b><small>{x.sub}</small></div>
          <Chip tone={x.tone}>{x.tone === 'bad' ? 'Overdue' : whenChip(today, x.date)}</Chip></div>
      ))}
    </Card>
  );
}

function TestsCard({ pd, store, today }: { pd: PersonData; store: BhStore; today: string }) {
  const all = testsOf(store, pd);
  const next = all.filter(t => t.status === 'scheduled' && t.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 2);
  const done = all.filter(t => t.status === 'done').sort((a, b) => b.date.localeCompare(a.date)).slice(0, 2);
  return (
    <Card title="🧪 Tests" link="Tests" onLink={() => go('test')}>
      {!next.length && !done.length && <div className="bh-dash-empty">No tests yet.</div>}
      {next.map(t => <div key={t.id} className="bh-dash-item"><DateBox iso={t.date} /><div className="bh-dash-iw"><b>{testNames(t)}</b><small>{[t.time && formatTime12(t.time), t.lab].filter(Boolean).join(' · ')}</small></div><Chip tone="blue">Scheduled</Chip></div>)}
      {done.map(t => <div key={t.id} className="bh-dash-item"><DateBox iso={t.date} /><div className="bh-dash-iw">
        <b>{(t.items || []).filter(i => String(i.value ?? '').trim()).map(i => `${i.name} ${i.value}${i.unit ? ' ' + i.unit : ''}`).join(' · ') || testNames(t)}</b><small>{t.lab || 'Results'}</small></div><Chip tone="ok">Done</Chip></div>)}
    </Card>
  );
}

function VaccinesCard({ pd, store, today }: { pd: PersonData; store: BhStore; today: string }) {
  const list = vaccinesDue(pd.person, vaxOf(store, pd), today, 365).slice(0, 3);
  return (
    <Card title="🛡️ Vaccines" link="Vaccines" onLink={() => go('vacc')}>
      {!list.length && <div className="bh-dash-empty">No vaccines due in the next year.</div>}
      {list.map(v => (
        <div key={v.key} className="bh-dash-item"><DateBox iso={v.status === 'overdue' ? null : v.due} />
          <div className="bh-dash-iw"><b>{v.name}{v.label ? ` · ${v.label}` : ''}</b><small>{v.status === 'overdue' ? `Overdue since ${fmtDayDMY(v.due)}` : fmtDayDMY(v.due)}</small></div>
          <Chip tone={v.status === 'overdue' ? 'bad' : v.status === 'due' ? 'warn' : 'grey'}>{v.status === 'overdue' ? 'Overdue' : v.status === 'due' ? 'Due soon' : 'Upcoming'}</Chip></div>
      ))}
    </Card>
  );
}

function VisitsCard({ pd, store, today }: { pd: PersonData; store: BhStore; today: string }) {
  const all = visitsOf(store, pd);
  const next = all.filter(v => (!v.status || v.status === 'scheduled') && v.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 2);
  const last = all.filter(v => v.status === 'completed').sort((a, b) => b.date.localeCompare(a.date)).slice(0, 1);
  return (
    <Card title="🩺 Doctor visits" link="Doctor Visits" onLink={() => go('visit')}>
      {!next.length && !last.length && <div className="bh-dash-empty">No doctor visits yet.</div>}
      {next.map(v => <div key={v.id} className="bh-dash-item"><DateBox iso={v.date} /><div className="bh-dash-iw"><b>{v.doctor || 'Doctor visit'}</b><small>{[v.time && formatTime12(v.time), v.specialty, v.clinic].filter(Boolean).join(' · ')}</small></div><Chip tone="blue">{whenChip(today, v.date)}</Chip></div>)}
      {last.map(v => <div key={v.id} className="bh-dash-item"><DateBox iso={v.date} /><div className="bh-dash-iw"><b>{v.doctor || 'Doctor visit'}</b><small>{[v.specialty, 'completed'].filter(Boolean).join(' · ')}</small></div><Chip tone="ok">Done</Chip></div>)}
    </Card>
  );
}

